import React, { useMemo, useCallback, useEffect } from 'react';
import { useModalStore, useAuthStore, useCreditPacksStore } from '@/store';
import ChatInputBanner, { ChatInputBannerAction } from '@/components/ChatInput/ChatInputBanner';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
  eventName,
} from '@/constants/utils/analyticsEvents';
import { trackEvent } from '@/constants/utils/analytics';
import useTranslation from '@/hooks/useTranslation';
import { formatCreditResetDate, startAryaChat } from '@/lib/plusCreditOptions';
import { formatCreditBalance } from '@/lib/formatCredits';
import { AUTO_TOPUP_BONUS_PERCENT, canNudgeAutoTopup } from '@/lib/autoTopupOffer';

const BANNER_SURFACE = 'chat_low_credits_banner';

function ChatInputLowCreditsBanner({ level, isCloseable = true, isDetached = false }) {
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable ?? 0);
  const creditsLabel = formatCreditBalance(totalAvailableCredits);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isEmailVerified = useAuthStore(state => state.user?.flags?.isEmailVerified);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  // Server-computed: credits this user would still receive by verifying their
  // email (signup verification top-up). 0 for legacy unverified users who
  // already got the full grant — they fall through to the standard upsell.
  const pendingVerificationCredits = useAuthStore(state => state.user?.credits?.pendingVerificationCredits ?? 0);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const resetDate = useAuthStore(state => state.user?.credits?.monthlyAllotment?.resetDate);
  const resetLabel = useMemo(() => formatCreditResetDate(resetDate), [resetDate]);
  
  // Check if user has stored payment method for quick top-up (US only)
  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const { usesWhopCheckout } = useGabPurchase();
  const user = useAuthStore(state => state.user);
  const canNudge = canNudgeAutoTopup({ user, usesWhopCheckout });
  
  // Get the most popular credit pack for quick top-up
  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const popularPack = useMemo(() => {
    if (!creditPacks?.length) return null;
    return creditPacks.find(p => p.flags?.isPopular) || creditPacks[0];
  }, [creditPacks]);

  // Can use quick top-up if has payment method and card data (US only)
  const canQuickTopUp = !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;

  useEffect(() => {
    trackConversionEvent(
      CONVERSION_ACTION.UPGRADE_BANNER_SHOWN,
      eventName(BANNER_SURFACE, isPlus ? 'plus' : 'free'),
      totalAvailableCredits,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The banner renders one of six variants, and which CTA a visitor took is
  // the whole question — so the variant travels in the event name rather than
  // collapsing every path into one "clicked".
  const trackBannerClick = useCallback((cta) => {
    trackConversionEvent(
      CONVERSION_ACTION.LOW_CREDITS_BANNER_CLICKED,
      eventName(BANNER_SURFACE, cta),
      totalAvailableCredits,
    );
    setCheckoutSourceDimension(BANNER_SURFACE);
  }, [totalAvailableCredits]);

  const handleUpgradePress = useCallback(() => {
    trackBannerClick('upgrade');
    openModal('PricingModal', {
      title: t('chatInput.credits.getFreeMessagesTitle'),
      subtitle: t('chatInput.credits.getFreeMessagesSubtitle'),
    });
  }, [openModal, t, trackBannerClick]);

  // Logged-out visitors don't have a credit balance to "run out" of — the
  // honest CTA is to create an account, not to upgrade or buy credits.
  const handleSignIn = useCallback(() => {
    trackBannerClick('sign_in');
    openModal('AuthCTAModal', {
      source: 'chat_low_credits_banner',
      title: t('chatInput.credits.signInToCreate') || 'Sign in to start creating',
      subtitle: t('chatInput.credits.signInToCreateSubtitle') || 'Create a free account to chat, generate images, and use every tool.',
      icon: 'sparkles',
    });
  }, [openModal, t, trackBannerClick]);

  const handleQuickTopUp = useCallback(() => {
    trackBannerClick('quick_top_up');
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal, trackBannerClick]);

  const handleAutoTopUp = useCallback(() => {
    trackBannerClick('auto_top_up');
    openModal('AutoTopUpConfigModal');
  }, [openModal, trackBannerClick]);

  const handleVerifyEmail = useCallback(() => {
    trackBannerClick('verify_email');
    openModal('VerifyEmailModal', { source: 'low_credits_banner' });
  }, [openModal, trackBannerClick]);

  // Plus at zero: the modal lays out every choice (Arya free / buy / Premium /
  // refresh date) instead of jumping straight to the credit store.
  const handleSeePlusOptions = useCallback(() => {
    trackEvent('conversion', 'low_credits_banner_see_options', 'plus');
    openModal('OutOfCreditsModal', { source: 'chat_banner' });
  }, [openModal]);

  const handleChatWithArya = useCallback(() => {
    startAryaChat({ source: 'chat_banner' });
  }, []);

  const action = (label, onPress, primary = false) => <ChatInputBannerAction key={label} onPress={onPress} primary={primary}>{label}</ChatInputBannerAction>;
  const notice = ({ message, title, icon, color = 'warning', actions }) => <ChatInputBanner
    color={color} level={level} isCloseable={isCloseable} isDetached={isDetached}
    title={title} icon={icon} actions={actions}>{message}</ChatInputBanner>;

  if (!hasUser) return notice({ color: 'info', icon: 'user',
    message: t(isXS ? 'chatInput.credits.signInToCreateShort' : 'chatInput.credits.signInToCreate'),
    actions: action(t('common.signIn'), handleSignIn),
  });

  if (!isPlus && !isGhost && !isEmailVerified) {
    const bonus = pendingVerificationCredits;
    return notice({ color: 'info', icon: 'envelope',
      title: bonus > 0 ? t('chatInput.credits.verificationRewardTitle').replace('%BONUS%', formatCreditBalance(bonus))
        : t('chatInput.credits.verificationTitle'),
      message: t(bonus > 0 ? 'chatInput.credits.verificationRewardDescription' : 'chatInput.credits.verifyToStart'),
      actions: action(t('chatInput.credits.verificationAction'), handleVerifyEmail, true),
    });
  }

  if (isPlus && canQuickTopUp && totalAvailableCredits > 0) return notice({
    message: t(isXS ? 'chatInput.credits.creditsLeft' : 'chatInput.credits.runningLow').replace('%COUNT%', creditsLabel),
    actions: isXS ? action(canNudge ? `Auto top-up +${AUTO_TOPUP_BONUS_PERCENT}%` : t('chatInput.credits.getCreditsLink'), canNudge ? handleAutoTopUp : handleQuickTopUp) : popularPack && <>
      {action(t('chatInput.credits.getCredits'), handleQuickTopUp)}
      {canNudge && action(`Auto top-up +${AUTO_TOPUP_BONUS_PERCENT}%`, handleAutoTopUp)}
    </>,
  });

  if (isPlus) {
    if (totalAvailableCredits > 0) return null;
    const message = isXS ? t('chatInput.credits.outOfCreditsShort') : resetLabel
      ? t('plusOutOfCredits.bannerOutOfCredits', { date: resetLabel }) : t('plusOutOfCredits.bannerOutOfCreditsNoDate');
    return notice({ color: 'danger', message, actions: <>
      {action(t('plusOutOfCredits.bannerChatArya'), handleChatWithArya)}
      {action(t(isXS ? 'chatInput.credits.getCreditsLink' : 'plusOutOfCredits.bannerSeeOptions'), handleSeePlusOptions)}
    </> });
  }

  if (canQuickTopUp) return notice({
    color: totalAvailableCredits <= 0 ? 'danger' : 'warning',
    message: totalAvailableCredits <= 0 ? t(isXS ? 'chatInput.credits.outOfCreditsShort' : 'chatInput.credits.outOfCredits')
      : t(isXS ? 'chatInput.credits.creditsLeft' : 'chatInput.credits.runningLow').replace('%COUNT%', creditsLabel),
    actions: isXS ? action(t('chatInput.credits.getCreditsLink'), handleQuickTopUp) : popularPack && <>
      {action(t('chatInput.credits.getCredits'), handleQuickTopUp)}
      {action(t('chatInput.credits.orUpgrade'), handleUpgradePress)}
    </>,
  });

  return notice({ color: totalAvailableCredits <= 0 ? 'danger' : 'warning',
    message: totalAvailableCredits <= 0 ? t(isXS ? 'chatInput.credits.outOfCreditsShort' : 'chatInput.credits.outOfCreditsUpgrade')
      : t(isXS ? 'chatInput.credits.creditsLeft' : 'chatInput.credits.runningLowPlus').replace('%COUNT%', creditsLabel),
    actions: action(t(totalAvailableCredits <= 0 ? 'chatInput.credits.upgradeNow' : 'chatInput.credits.getFreeMessages'), handleUpgradePress),
  });
}

export default React.memo(ChatInputLowCreditsBanner);
